import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Plus, Pencil, Trash2, Loader2, ImagePlus, Layers, EyeOff, Eye, X, Save, Upload } from 'lucide-react';
import {
  listVerticals,
  createVertical,
  updateVertical,
  deleteVertical,
  uploadFile,
  resolveFileUrl,
} from '../../services/api';
import { toast } from '../../hooks/use-toast';

const emptyForm = {
  title: '',
  eyebrow: '',
  slug: '',
  brief: '',
  details: '',
  image_url: '',
  order: 0,
  active: true,
};

const VerticalsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null or vertical object
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listVerticals();
      setItems(res.data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, order: (items.length || 0) + 1 });
    setDrawerOpen(true);
  };

  const openEdit = (v) => {
    setEditing(v);
    setForm({
      title: v.title || '',
      eyebrow: v.eyebrow || '',
      slug: v.slug || '',
      brief: v.brief || '',
      details: v.details || '',
      image_url: v.image_url || '',
      order: v.order || 0,
      active: v.active !== false,
    });
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setProgress(0);
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setProgress(0);
    try {
      const res = await uploadFile(file, setProgress);
      setForm((f) => ({ ...f, image_url: res.url }));
      toast({ title: 'Image uploaded', description: file.name });
    } catch (err) {
      toast({
        title: 'Upload failed',
        description: err?.response?.data?.detail || 'Please retry.',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await updateVertical(editing.id, form);
        toast({ title: 'Vertical updated' });
      } else {
        await createVertical(form);
        toast({ title: 'Vertical created' });
      }
      closeDrawer();
      load();
    } catch (err) {
      toast({
        title: 'Save failed',
        description: err?.response?.data?.detail || 'Check the form and try again.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete "${v.title}"? This cannot be undone.`)) return;
    try {
      await deleteVertical(v.id);
      load();
    } catch (err) {
      toast({ title: 'Delete failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    }
  };

  return (
    <div>
      <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
        <div>
          <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">CMS</span>
          <h1
            className="mt-2 text-4xl text-[#0b1a2e]"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}
          >
            Business Verticals
          </h1>
          <p className="mt-2 text-sm text-[#0b1a2e]/60">Add, edit and reorder verticals shown across the public site.</p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#0f5c3d] text-white text-sm font-medium hover:bg-[#0b1a2e] transition"
        >
          <Plus className="w-4 h-4" /> New Vertical
        </button>
      </div>

      {loading ? (
        <div className="bg-white border border-black/5 rounded-2xl p-10 text-center text-[#0b1a2e]/60">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading verticals…
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-black/5 rounded-2xl p-10 text-center">
          <Layers className="w-10 h-10 mx-auto text-[#0b1a2e]/30 mb-3" />
          <p className="text-[#0b1a2e]/60 mb-4">No verticals yet. Add your first one.</p>
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0f5c3d] text-white text-sm"
          >
            <Plus className="w-4 h-4" /> Add vertical
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((v) => (
            <div key={v.id} className="bg-white border border-black/5 rounded-2xl overflow-hidden hover:shadow-md transition">
              <div className="aspect-[16/10] bg-[#e2ede6] relative overflow-hidden">
                {v.image_url ? (
                  <img src={resolveFileUrl(v.image_url)} alt={v.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="flex items-center justify-center h-full text-[#0b1a2e]/30">
                    <ImagePlus className="w-8 h-8" />
                  </div>
                )}
                {!v.active && (
                  <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/90 text-[10px] uppercase tracking-widest text-[#0b1a2e]/70">
                    <EyeOff className="w-3 h-3" /> Hidden
                  </span>
                )}
              </div>
              <div className="p-5">
                {v.eyebrow && (
                  <div className="text-[10px] font-semibold tracking-[0.25em] uppercase text-[#0f5c3d]">{v.eyebrow}</div>
                )}
                <h3
                  className="mt-1 text-xl text-[#0b1a2e]"
                  style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}
                >
                  {v.title}
                </h3>
                <p className="mt-2 text-sm text-[#0b1a2e]/60 line-clamp-3">{v.brief}</p>
                <div className="mt-4 flex items-center justify-between text-xs text-[#0b1a2e]/50">
                  <span>Order: {v.order ?? 0}</span>
                  <span>/verticals/{v.slug}</span>
                </div>
                <div className="mt-4 flex items-center gap-2">
                  <button
                    onClick={() => openEdit(v)}
                    className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg border border-[#0f5c3d]/20 text-[#0b1a2e] text-xs font-medium hover:bg-[#f7fbf8]"
                  >
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button
                    onClick={() => remove(v)}
                    className="inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-red-600 hover:bg-red-50 text-xs font-medium"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-40 flex justify-end">
          <div className="absolute inset-0 bg-black/40" onClick={closeDrawer} />
          <form
            onSubmit={save}
            className="relative bg-white w-full sm:max-w-xl h-full overflow-y-auto shadow-2xl flex flex-col"
          >
            <div className="px-6 py-4 border-b border-black/5 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#0f5c3d] font-semibold">
                  {editing ? 'Edit vertical' : 'New vertical'}
                </div>
                <div className="text-lg font-semibold text-[#0b1a2e]">{form.title || 'Untitled'}</div>
              </div>
              <button type="button" onClick={closeDrawer} className="p-2 hover:bg-black/5 rounded-lg">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-6 space-y-5 flex-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Title</span>
                  <input
                    required
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0f5c3d]/30"
                  />
                </label>
                <label className="block">
                  <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Eyebrow</span>
                  <input
                    value={form.eyebrow}
                    onChange={(e) => setForm({ ...form, eyebrow: e.target.value })}
                    placeholder="e.g. Precious Metals"
                    className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0f5c3d]/30"
                  />
                </label>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <label className="block sm:col-span-2">
                  <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Slug</span>
                  <input
                    value={form.slug}
                    onChange={(e) => setForm({ ...form, slug: e.target.value })}
                    placeholder="auto-generated if empty"
                    className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0f5c3d]/30"
                  />
                </label>
                <label className="block">
                  <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Order</span>
                  <input
                    type="number"
                    value={form.order}
                    onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
                    className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0f5c3d]/30"
                  />
                </label>
              </div>

              <label className="block">
                <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Brief description</span>
                <textarea
                  required
                  rows={2}
                  value={form.brief}
                  onChange={(e) => setForm({ ...form, brief: e.target.value })}
                  className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0f5c3d]/30 resize-y"
                />
              </label>

              <label className="block">
                <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Detailed description</span>
                <textarea
                  required
                  rows={8}
                  value={form.details}
                  onChange={(e) => setForm({ ...form, details: e.target.value })}
                  placeholder="Rich text. Newlines are preserved on the public page."
                  className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0f5c3d]/30 resize-y"
                />
              </label>

              <div>
                <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Image</span>
                <div className="flex items-start gap-4">
                  <div className="w-40 h-28 rounded-lg overflow-hidden bg-[#e2ede6] flex items-center justify-center shrink-0">
                    {form.image_url ? (
                      <img src={resolveFileUrl(form.image_url)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <ImagePlus className="w-6 h-6 text-[#0b1a2e]/30" />
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleUpload(e.target.files?.[0])}
                    />
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[#0f5c3d]/20 text-sm text-[#0b1a2e] hover:bg-[#f7fbf8] disabled:opacity-60"
                    >
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload
                    </button>
                    {uploading && (
                      <div className="w-full h-1.5 bg-[#e2ede6] rounded-full overflow-hidden">
                        <div className="h-full bg-[#0f5c3d] transition-all" style={{ width: `${progress}%` }} />
                      </div>
                    )}
                    <input
                      value={form.image_url}
                      onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                      placeholder="or paste an image URL"
                      className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-xs outline-none"
                    />
                  </div>
                </div>
              </div>

              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                  className="w-4 h-4 accent-[#0f5c3d]"
                />
                <span className="text-sm text-[#0b1a2e]">Published (visible on public site)</span>
              </label>
            </div>

            <div className="px-6 py-4 border-t border-black/5 bg-[#f7fbf8] flex items-center justify-end gap-2">
              <button type="button" onClick={closeDrawer} className="px-4 py-2 rounded-lg text-sm text-[#0b1a2e]/70 hover:bg-black/5">
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-[#0f5c3d] text-white text-sm font-medium hover:bg-[#0b1a2e] transition disabled:opacity-60"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {editing ? 'Save changes' : 'Create vertical'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default VerticalsPage;
